const locale = import.meta.env?.VITE_LOCALE || "es-AR";
const currency = import.meta.env?.VITE_CURRENCY || "ARS";

// Sin centavos: se trabaja en pesos, donde los importes van de los miles a los
// millones. Mostrar ",00" en cada cifra sólo ensancha las columnas y hace más
// difícil comparar de un vistazo. Los centavos que hubiera en la base se
// siguen guardando y sumando; sólo no se dibujan.
const currencyFormatter = new Intl.NumberFormat(locale, {
  style: "currency",
  currency,
  maximumFractionDigits: 0,
});

export function formatCurrency(value) {
  return currencyFormatter.format(Number(value) || 0);
}

const compactNumber = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });

/**
 * Importe abreviado, para ejes de gráficos y lugares angostos donde
 * "$ 12.345.678" no entra: 12,3 M · 850 k · 1.200.
 */
export function formatCompact(value) {
  const n = Number(value) || 0;
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${compactNumber.format(n / 1_000_000)} M`;
  if (abs >= 10_000) return `${compactNumber.format(Math.round(n / 1000))} k`;
  return compactNumber.format(Math.round(n));
}

export function formatDate(value) {
  if (!value) return "";
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
}

export function monthLabel(year, month) {
  const date = new Date(year, month - 1, 1);
  return date.toLocaleDateString(locale, { month: "short", year: "numeric" });
}

export function currentYearMonth(date = new Date()) {
  return { year: date.getFullYear(), month: date.getMonth() + 1 };
}

/**
 * Fecha en formato YYYY-MM-DD según el reloj local.
 *
 * No usar `toISOString().slice(0, 10)` para esto: convierte a UTC, así que la
 * medianoche local de un huso adelantado (Europa) cae el día anterior, y la
 * noche de un huso atrasado (Argentina) cae el día siguiente. En una app que
 * guarda fechas de eventos, eso se traduce en eventos corridos un día.
 */
export function toLocalISODate(date = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Lee un importe escrito a la argentina: el punto separa miles y la coma es
 * el decimal. "1.250.000" → 1250000 · "250.000,50" → 250000.5
 *
 * Hace falta porque <input type="number"> interpreta el punto como decimal:
 * quien escribía "250.000" pensando en 250 mil guardaba 250 pesos.
 */
export function parseAmount(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;

  const cleaned = String(value ?? "").trim().replace(/[^\d.,-]/g, "");
  if (!cleaned) return 0;

  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");
  let normalized;

  if (lastComma > -1) {
    // Hay coma: es el decimal, y todos los puntos son de miles.
    normalized = cleaned.replace(/\./g, "").replace(",", ".");
  } else if (lastDot > -1 && cleaned.length - lastDot - 1 !== 3) {
    // Un punto que no deja tres dígitos atrás no puede ser separador de
    // miles, así que viene de escribir decimales a la inglesa: se respeta.
    normalized = cleaned;
  } else {
    // Puntos de miles: "250.000", "1.250.000".
    normalized = cleaned.replace(/\./g, "");
  }

  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
}

/** El importe tal como se escribe en el campo: 1.250.000 (sin símbolo). */
export function formatAmountInput(value) {
  const n = Number(value) || 0;
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(n);
}

/** "15:00:00" → "15:00". Vacío si no hay hora. */
export function formatTime(value) {
  if (!value) return "";
  return String(value).slice(0, 5);
}

/** "15:00–18:00", "15:00", o "" si no hay hora. */
export function formatTimeRange(start, end) {
  if (!start) return "";
  return end ? `${formatTime(start)}–${formatTime(end)}` : formatTime(start);
}
